import { collection } from '../common';
import {
  type Command,
  type EditorState,
  type EditorView,
  Plugin,
  PluginKey,
  TextSelection,
} from '../pm';
import {
  createVirtualElementFromRange,
  findParentNodeOfType,
  type VirtualElement,
} from '../pm-utils';
import { store } from '../store';

export type CalloutMenuState = {
  show: boolean;
  /** Document position of the callout node (for React remount keys). */
  calloutPos: number;
  anchorEl: () => VirtualElement | null;
  /**
   * When true, the React toolbar should focus the title field once (e.g. after
   * the user clicks the rendered callout title).
   */
  focusTitle?: boolean;
};

type DismissedCallout = {
  calloutPos: number;
};

const calloutMenuKey = new PluginKey<DismissedCallout | undefined>(
  'callout-menu',
);
const dismissMeta = 'dismiss-callout-menu';
const CALLOUT_NODE_NAME = 'callout';

type CalloutMenuStates = ReadonlyMap<EditorView, CalloutMenuState>;

const $internalCalloutMenu = store.atom<CalloutMenuStates>(new Map());

export const $calloutMenu = store.atom((get) => get($internalCalloutMenu));

function setCalloutMenuForView(
  state: EditorState,
  view: EditorView,
  value: CalloutMenuState | undefined,
) {
  const current = store.get(state, $internalCalloutMenu);
  if (value === undefined && !current.has(view)) {
    return;
  }

  const next = new Map(current);
  if (value) {
    next.set(view, value);
  } else {
    next.delete(view);
  }
  store.set(state, $internalCalloutMenu, next);
}

function findCalloutAroundSelection(state: EditorState) {
  const type = state.schema.nodes[CALLOUT_NODE_NAME];
  if (!type) {
    return undefined;
  }
  return findParentNodeOfType(type)(state.selection);
}

function findCalloutPosFromCoords(
  view: EditorView,
  clientX: number,
  clientY: number,
): number | undefined {
  const coords = view.posAtCoords({ left: clientX, top: clientY });
  if (!coords) {
    return undefined;
  }
  const $pos = view.state.doc.resolve(coords.inside ?? coords.pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === CALLOUT_NODE_NAME) {
      return $pos.before(depth);
    }
  }
  return undefined;
}

/**
 * Tracks whether the selection sits inside a callout and exposes its
 * position and DOM anchor so React can render type/title controls.
 */
function calloutMenuPlugin() {
  return new Plugin<DismissedCallout | undefined>({
    key: calloutMenuKey,
    state: {
      init: () => undefined,
      apply(transaction, dismissed, _oldState, newState) {
        const meta = transaction.getMeta(calloutMenuKey) as
          | typeof dismissMeta
          | undefined;
        if (meta === dismissMeta) {
          const found = findCalloutAroundSelection(newState);
          if (!found) {
            return undefined;
          }
          return { calloutPos: found.pos };
        }
        if (dismissed && !transaction.doc.eq(transaction.before)) {
          return undefined;
        }
        return dismissed;
      },
    },
    props: {
      handleKeyDown(view, event) {
        if (event.key !== 'Escape') {
          return false;
        }
        if (!store.get(view.state, $internalCalloutMenu).has(view)) {
          return false;
        }
        view.dispatch(view.state.tr.setMeta(calloutMenuKey, dismissMeta));
        return true;
      },
      handleDOMEvents: {
        // Title chrome is contenteditable=false, so clicks don't place a
        // caret. Treat them as "rename this callout" and open the title field.
        click(view, event) {
          if (!view.editable) {
            return false;
          }
          const target = event.target;
          if (!(target instanceof Element)) {
            return false;
          }
          if (!target.closest('.bangle-callout-title')) {
            return false;
          }
          if (!view.dom.contains(target)) {
            return false;
          }

          const calloutPos = findCalloutPosFromCoords(
            view,
            event.clientX,
            event.clientY,
          );
          if (calloutPos == null) {
            return false;
          }
          const callout = view.state.doc.nodeAt(calloutPos);
          if (!callout || callout.type.name !== CALLOUT_NODE_NAME) {
            return false;
          }

          const sel = TextSelection.near(
            view.state.doc.resolve(calloutPos + 1),
            1,
          );
          view.dispatch(view.state.tr.setSelection(sel));

          const anchorEl = () =>
            createVirtualElementFromRange(
              view,
              calloutPos,
              calloutPos + callout.nodeSize,
            );
          setCalloutMenuForView(view.state, view, {
            show: true,
            calloutPos,
            anchorEl,
            focusTitle: true,
          });
          event.preventDefault();
          return true;
        },
      },
    },
    view(view) {
      const clearOwnedState = () =>
        setCalloutMenuForView(view.state, view, undefined);

      const sync = () => {
        const { state } = view;

        if (!view.editable) {
          clearOwnedState();
          return;
        }

        const found = findCalloutAroundSelection(state);
        if (!found) {
          clearOwnedState();
          return;
        }

        const calloutPos = found.pos;
        const dismissed = calloutMenuKey.getState(state);
        if (dismissed?.calloutPos === calloutPos) {
          clearOwnedState();
          return;
        }

        const callout = state.doc.nodeAt(calloutPos);
        if (!callout) {
          clearOwnedState();
          return;
        }

        const previous = store.get(state, $internalCalloutMenu).get(view);
        const anchorEl = () =>
          createVirtualElementFromRange(
            view,
            calloutPos,
            calloutPos + callout.nodeSize,
          );
        if (!anchorEl()) {
          clearOwnedState();
          return;
        }

        setCalloutMenuForView(state, view, {
          show: true,
          calloutPos,
          anchorEl,
          // Keep a pending title-focus request across selection syncs for the
          // same callout until React consumes it.
          focusTitle:
            previous?.calloutPos === calloutPos
              ? previous.focusTitle
              : undefined,
        });
      };

      sync();

      return {
        update: sync,
        destroy: clearOwnedState,
      };
    },
  });
}

/** Hides the callout menu for the active callout until the document changes. */
function dismissCalloutMenu(): Command {
  return (state, dispatch, view) => {
    const menus = store.get(state, $internalCalloutMenu);
    const owner =
      view && view.state === state
        ? view
        : [...menus.keys()].find((candidate) => candidate.state === state);
    if (!owner || !menus.has(owner)) {
      return false;
    }
    dispatch?.(state.tr.setMeta(calloutMenuKey, dismissMeta));
    return true;
  };
}

/** Clears the one-shot "focus the title field" flag after React handles it. */
function clearCalloutTitleFocus(): Command {
  return (state, _dispatch, view) => {
    const menus = store.get(state, $internalCalloutMenu);
    const owner =
      view && view.state === state
        ? view
        : [...menus.keys()].find((candidate) => candidate.state === state);
    if (!owner) {
      return false;
    }
    const current = menus.get(owner);
    if (!current?.focusTitle) {
      return false;
    }
    setCalloutMenuForView(state, owner, { ...current, focusTitle: false });
    return true;
  };
}

export function setupCalloutMenu() {
  return collection({
    id: 'callout-menu',
    plugin: {
      calloutMenu: calloutMenuPlugin(),
    },
    command: {
      dismissCalloutMenu,
      clearCalloutTitleFocus,
    },
    $calloutMenu,
  });
}
